import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faLifeRing } from "@fortawesome/pro-duotone-svg-icons/faLifeRing";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlows } from "@frigade/react";
import cn from "classnames";
import { useIntl } from "react-intl";

import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button } from "@mapmycustomers/ui";

import LoadingDots from "@app/component/LoadingDots";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import RoundButton from "@app/component/RoundButton";
import { showSidebar } from "@app/scene/map/store/mapMode/actions";
import { getTotalFilteredRecordsCount, isLoading } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import frigadeService from "@app/util/frigade/frigadeService";

import styles from "./LoadingStatusPanel.module.scss";

interface Props {
  className?: string;
  loading: boolean;
  recordsCount: number;
  showSidebar: typeof showSidebar;
}

const LoadingStatusPanel: React.FC<Props> = ({ className, loading, recordsCount, showSidebar }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const handleOpenSidebar = useCallback(() => {
    analytics.clicked(["Pins in view button"]);
    showSidebar();
  }, [analytics, showSidebar]);

  const { markFlowCompleted, markFlowStarted } = useFlows();
  const handleGuideClick = useDynamicCallback(async () => {
    // hiding the guide button tooltip, guess we don't need it since user clicked on a button
    await markFlowCompleted("flow_tcPQNQor");
    frigadeService.hideFlow("flow_tcPQNQor");

    await markFlowStarted("flow_koqBKw7S8iMTCFyT");
    frigadeService.showFlow("flow_koqBKw7S8iMTCFyT");
  });

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.content}>
        {loading ? (
          <LoadingDots />
        ) : (
          <Interactive>
            <Button block className={styles.btn} onClick={handleOpenSidebar}>
              {intl.formatMessage(
                {
                  id: "map.toolbar.statusPanel.count",
                  defaultMessage:
                    "{value, plural, =0 {No results} one {{value} pin in view} other {{value} pins in view}}",
                  description: "Record count label title",
                },
                { value: recordsCount }
              )}
            </Button>
          </Interactive>
        )}
      </div>
      <Interactive>
        <RoundButton
          active
          className={styles.guideButton}
          icon={<FontAwesomeIcon icon={faLifeRing} />}
          onClick={handleGuideClick}
          size="large"
        >
          {intl.formatMessage({
            id: "map.toolbar.guide",
            defaultMessage: "Guide",
            description: "Guide button which open onboarding flow for the Map page",
          })}
        </RoundButton>
      </Interactive>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isLoading(state),
  recordsCount: getTotalFilteredRecordsCount(state),
});

const mapDispatchToProps = {
  showSidebar,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoadingStatusPanel);
